import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Squares } from '@/components/ui/squares-background';
import { 
  MousePointer, 
  Type, 
  Square, 
  Circle, 
  Image as ImageIcon,
  RotateCw,
  Move,
  ZoomIn,
  ZoomOut,
  Layers,
  ArrowUp,
  ArrowDown,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface Editor {
  addImageLayer: (data: any) => Promise<void>;
  addTextLayer: (data: any) => Promise<void>;
  addShapeLayer: (data: any) => Promise<void>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  export: (options: any) => Promise<string>;
  toJSON: () => Promise<any>;
  loadFromJSON: (data: any) => Promise<void>;
  destroy: () => void;
}

interface CanvasElement {
  id: string;
  type: 'image' | 'text' | 'shape';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  selected?: boolean;
  // Para texto
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  // Para imagem
  src?: string;
  // Para forma
  shape?: string;
  backgroundColor?: string;
  borderRadius?: number;
}

interface AdvancedCanvasEditorProps {
  onEditorReady?: (editor: Editor) => void;
  onDesignChange?: (designUrl: string, elements: CanvasElement[]) => void;
  width?: number;
  height?: number;
}

const EditorContainer = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
`;

const ToolSection = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ToolButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem;
  border: 1px solid ${props => props.active ? '#3B82F6' : '#e5e7eb'};
  border-radius: 6px;
  background: ${props => props.active ? '#3B82F6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? '#2563EB' : '#f3f4f6'};
  }
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  position: relative;
`;

const CanvasArea = styled.div<{ layersVisible: boolean }>`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  width: ${props => props.layersVisible ? 'calc(100% - 200px)' : '100%'};
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
`;

const Canvas = styled.div`
  width: 400px;
  height: 300px;
  background: white;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  cursor: crosshair;
  z-index: 1;
`;

const CanvasElement = styled.div<{ 
  selected?: boolean; 
  isDragging?: boolean; 
  isResizing?: boolean;
  rotation: number;
  zIndex: number;
}>`
  position: absolute;
  border: ${props => props.selected ? '2px solid #3B82F6' : '2px solid transparent'};
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  transform: rotate(${props => props.rotation}deg);
  z-index: ${props => props.zIndex};
  
  &:hover {
    border-color: ${props => props.selected ? '#3B82F6' : '#93c5fd'};
  }
`;

const ResizeHandle = styled.div<{ position: string }>`
  position: absolute;
  width: 12px;
  height: 12px;
  background: #3B82F6;
  border: 2px solid white;
  border-radius: 50%;
  cursor: ${props => {
    switch (props.position) {
      case 'nw': return 'nw-resize';
      case 'ne': return 'ne-resize';
      case 'sw': return 'sw-resize';
      case 'se': return 'se-resize';
      default: return 'pointer';
    }
  }};
  
  ${props => {
    switch (props.position) {
      case 'nw': return 'top: -6px; left: -6px;';
      case 'ne': return 'top: -6px; right: -6px;';
      case 'sw': return 'bottom: -6px; left: -6px;';
      case 'se': return 'bottom: -6px; right: -6px;';
      default: return '';
    }
  }}
  
  &:hover {
    background: #2563EB;
    transform: scale(1.2);
  }
`;

const RotateHandle = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 16px;
  background: #10b981;
  border: 2px solid white;
  border-radius: 50%;
  cursor: grab;
  
  &:hover {
    background: #059669;
    transform: translateX(-50%) scale(1.2);
  }
`;

const PropertyPanel = styled.div`
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const LayersPanel = styled.div<{ visible: boolean }>`
  width: ${props => props.visible ? '200px' : '0'};
  border-left: ${props => props.visible ? '1px solid #e5e7eb' : 'none'};
  background: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
`;

const LayersToggle = styled.button<{ visible: boolean }>`
  position: absolute;
  right: ${props => props.visible ? '200px' : '0'};
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px 0 0 6px;
  padding: 0.5rem;
  z-index: 101;
  transition: right 0.3s ease;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const LayerItem = styled.div<{ selected?: boolean }>`
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: ${props => props.selected ? '#f3f4f6' : 'white'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #1f2937;
  font-weight: 500;
  
  &:hover {
    background: #f9fafb;
  }
`;

const StyledInput = styled(Input)`
  color: #1f2937;
  background: white;
  border: 1px solid #d1d5db;
  
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StyledSelect = styled.div`
  .select-trigger {
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    
    &:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const AdvancedCanvasEditor: React.FC<AdvancedCanvasEditorProps> = ({ 
  onEditorReady, 
  onDesignChange,
  width = 800, 
  height = 600 
}) => {
  const [activeTool, setActiveTool] = useState<string>('select');
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string>('');
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [textInput, setTextInput] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [textColor, setTextColor] = useState('#000000');
  const [shapeColor, setShapeColor] = useState('#3B82F6');
  const [nextZIndex, setNextZIndex] = useState(1);
  const [layersVisible, setLayersVisible] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);

  // Função para exportar o design e notificar mudanças
  const exportAndNotify = async () => {
    if (!canvasRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      ctx.fillStyle = 'transparent';
      ctx.fillRect(0, 0, 400, 300);
      
      // Ordenar elementos por zIndex
      const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);
      
      for (const element of sortedElements) {
        ctx.save();
        ctx.translate(element.x + element.width/2, element.y + element.height/2);
        ctx.rotate((element.rotation * Math.PI) / 180);
        ctx.translate(-element.width/2, -element.height/2);
        
        if (element.type === 'text') {
          ctx.fillStyle = element.color || 'black';
          ctx.font = `${element.fontSize || 16}px ${element.fontFamily || 'Arial'}`;
          ctx.fillText(element.content || '', 0, element.fontSize || 16);
        } else if (element.type === 'shape') {
          ctx.fillStyle = element.backgroundColor || 'blue';
          if (element.shape === 'circle') {
            ctx.beginPath();
            ctx.arc(element.width/2, element.height/2, element.width/2, 0, 2 * Math.PI);
            ctx.fill();
          } else {
            ctx.fillRect(0, 0, element.width, element.height);
          }
        } else if (element.type === 'image' && element.src) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            ctx.drawImage(img, 0, 0, element.width, element.height);
            // Re-export after image loads
            setTimeout(() => {
              const dataUrl = canvas.toDataURL('image/png');
              onDesignChange?.(dataUrl, elements);
            }, 100);
          };
          img.src = element.src;
        }
        ctx.restore();
      }
      
      const dataUrl = canvas.toDataURL('image/png');
      onDesignChange?.(dataUrl, elements);
    }
  };

  // Exportar sempre que os elementos mudarem
  useEffect(() => {
    if (elements.length > 0) {
      exportAndNotify();
    }
  }, [elements]);

  useEffect(() => {
    const mockEditor: Editor = {
      addImageLayer: async (data: any) => {
        const newElement: CanvasElement = {
          id: `img-${Date.now()}`,
          type: 'image',
          src: data.image.url,
          x: data.position.x,
          y: data.position.y,
          width: data.size.width,
          height: data.size.height,
          rotation: 0,
          zIndex: nextZIndex
        };
        setElements(prev => [...prev, newElement]);
        setNextZIndex(prev => prev + 1);
      },
      addTextLayer: async (data: any) => {
        const newElement: CanvasElement = {
          id: `text-${Date.now()}`,
          type: 'text',
          content: data.text,
          x: data.position.x,
          y: data.position.y,
          width: 200,
          height: 30,
          rotation: 0,
          zIndex: nextZIndex,
          fontSize: data.style.fontSize,
          color: data.style.color,
          fontFamily: data.style.fontFamily
        };
        setElements(prev => [...prev, newElement]);
        setNextZIndex(prev => prev + 1);
      },
      addShapeLayer: async (data: any) => {
        const newElement: CanvasElement = {
          id: `shape-${Date.now()}`,
          type: 'shape',
          shape: data.shape,
          x: data.position.x,
          y: data.position.y,
          width: data.size.width,
          height: data.size.height,
          rotation: 0,
          zIndex: nextZIndex,
          backgroundColor: data.style.backgroundColor,
          borderRadius: data.style.borderRadius
        };
        setElements(prev => [...prev, newElement]);
        setNextZIndex(prev => prev + 1);
      },
      undo: async () => {
        setElements(prev => prev.slice(0, -1));
      },
      redo: async () => {},
      export: async (options: any) => {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.fillStyle = 'transparent';
          ctx.fillRect(0, 0, 400, 300);
          
          const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);
          
          for (const element of sortedElements) {
            ctx.save();
            ctx.translate(element.x + element.width/2, element.y + element.height/2);
            ctx.rotate((element.rotation * Math.PI) / 180);
            ctx.translate(-element.width/2, -element.height/2);
            
            if (element.type === 'text') {
              ctx.fillStyle = element.color || 'black';
              ctx.font = `${element.fontSize || 16}px ${element.fontFamily || 'Arial'}`;
              ctx.fillText(element.content || '', 0, element.fontSize || 16);
            } else if (element.type === 'shape') {
              ctx.fillStyle = element.backgroundColor || 'blue';
              if (element.shape === 'circle') {
                ctx.beginPath();
                ctx.arc(element.width/2, element.height/2, element.width/2, 0, 2 * Math.PI);
                ctx.fill();
              } else {
                ctx.fillRect(0, 0, element.width, element.height);
              }
            }
            ctx.restore();
          }
        }
        
        return canvas.toDataURL('image/png');
      },
      toJSON: async () => ({ version: '1.0', objects: elements }),
      loadFromJSON: async (data: any) => setElements(data.objects || []),
      destroy: () => {}
    };
    
    onEditorReady?.(mockEditor);
  }, [elements, onEditorReady, nextZIndex]);

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (activeTool === 'text' && textInput.trim()) {
      const newElement: CanvasElement = {
        id: `text-${Date.now()}`,
        type: 'text',
        content: textInput,
        x,
        y,
        width: 200,
        height: 30,
        rotation: 0,
        zIndex: nextZIndex,
        fontSize,
        fontFamily,
        color: textColor
      };
      setElements(prev => [...prev, newElement]);
      setNextZIndex(prev => prev + 1);
      setTextInput('');
    } else if (activeTool === 'rectangle') {
      const newElement: CanvasElement = {
        id: `rect-${Date.now()}`,
        type: 'shape',
        shape: 'rectangle',
        x,
        y,
        width: 100,
        height: 60,
        rotation: 0,
        zIndex: nextZIndex,
        backgroundColor: shapeColor
      };
      setElements(prev => [...prev, newElement]);
      setNextZIndex(prev => prev + 1);
    } else if (activeTool === 'circle') {
      const newElement: CanvasElement = {
        id: `circle-${Date.now()}`,
        type: 'shape',
        shape: 'circle',
        x,
        y,
        width: 80,
        height: 80,
        rotation: 0,
        zIndex: nextZIndex,
        backgroundColor: shapeColor,
        borderRadius: 50
      };
      setElements(prev => [...prev, newElement]);
      setNextZIndex(prev => prev + 1);
    }
  };

  const handleElementMouseDown = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    setSelectedElement(elementId);
    setIsDragging(true);
    
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleResizeStart = (e: React.MouseEvent, elementId: string, handle: string) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setSelectedElement(elementId);
    
    const element = elements.find(el => el.id === elementId);
    if (element) {
      setInitialSize({ width: element.width, height: element.height });
    }
    
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleRotateStart = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    setIsRotating(true);
    setSelectedElement(elementId);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    if (isDragging && selectedElement && !isResizing) {
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;
      
      setElements(prev => prev.map(el => 
        el.id === selectedElement 
          ? { ...el, x: Math.max(0, el.x + deltaX), y: Math.max(0, el.y + deltaY) }
          : el
      ));
      
      setDragStart({ x: currentX, y: currentY });
    } else if (isResizing && selectedElement) {
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;
      
      setElements(prev => prev.map(el => {
        if (el.id !== selectedElement) return el;
        
        let newWidth = el.width;
        let newHeight = el.height;
        let newX = el.x;
        let newY = el.y;
        
        switch (resizeHandle) {
          case 'se':
            newWidth = Math.max(20, initialSize.width + deltaX);
            newHeight = Math.max(20, initialSize.height + deltaY);
            break;
          case 'sw':
            newWidth = Math.max(20, initialSize.width - deltaX);
            newHeight = Math.max(20, initialSize.height + deltaY);
            newX = el.x + (el.width - newWidth);
            break;
          case 'ne':
            newWidth = Math.max(20, initialSize.width + deltaX);
            newHeight = Math.max(20, initialSize.height - deltaY);
            newY = el.y + (el.height - newHeight);
            break;
          case 'nw':
            newWidth = Math.max(20, initialSize.width - deltaX);
            newHeight = Math.max(20, initialSize.height - deltaY);
            newX = el.x + (el.width - newWidth);
            newY = el.y + (el.height - newHeight);
            break;
        }
        
        return { ...el, width: newWidth, height: newHeight, x: newX, y: newY };
      }));
    } else if (isRotating && selectedElement) {
      const element = elements.find(el => el.id === selectedElement);
      if (element) {
        const centerX = element.x + element.width / 2;
        const centerY = element.y + element.height / 2;
        const angle = Math.atan2(currentY - centerY, currentX - centerX) * (180 / Math.PI);
        
        setElements(prev => prev.map(el => 
          el.id === selectedElement 
            ? { ...el, rotation: angle }
            : el
        ));
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setIsRotating(false);
    setResizeHandle('');
  };

  const updateSelectedElement = (updates: Partial<CanvasElement>) => {
    if (!selectedElement) return;
    
    setElements(prev => prev.map(el => 
      el.id === selectedElement ? { ...el, ...updates } : el
    ));
  };

  const moveElementUp = (elementId: string) => {
    setElements(prev => prev.map(el => 
      el.id === elementId ? { ...el, zIndex: el.zIndex + 1 } : el
    ));
  };

  const moveElementDown = (elementId: string) => {
    setElements(prev => prev.map(el => 
      el.id === elementId ? { ...el, zIndex: Math.max(0, el.zIndex - 1) } : el
    ));
  };

  const deleteElement = (elementId: string) => {
    setElements(prev => prev.filter(el => el.id !== elementId));
    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
  };

  const selectedElementData = elements.find(el => el.id === selectedElement);
  const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

  const renderElement = (element: CanvasElement) => {
    const isSelected = selectedElement === element.id;
    
    return (
      <CanvasElement
        key={element.id}
        selected={isSelected}
        isDragging={isDragging && isSelected}
        rotation={element.rotation}
        zIndex={element.zIndex}
        style={{
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height
        }}
        onMouseDown={(e) => handleElementMouseDown(e, element.id)}
      >
        {element.type === 'image' && (
          <img 
            src={element.src} 
            alt="Design element"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            draggable={false}
          />
        )}
        
        {element.type === 'text' && (
          <div style={{
            fontSize: element.fontSize,
            color: element.color,
            fontFamily: element.fontFamily,
            padding: '4px',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            fontWeight: 500
          }}>
            {element.content}
          </div>
        )}
        
        {element.type === 'shape' && (
          <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: element.backgroundColor,
            borderRadius: element.borderRadius || 0
          }} />
        )}
        
        {isSelected && (
          <>
            <ResizeHandle 
              position="nw" 
              onMouseDown={(e) => handleResizeStart(e, element.id, 'nw')}
            />
            <ResizeHandle 
              position="ne" 
              onMouseDown={(e) => handleResizeStart(e, element.id, 'ne')}
            />
            <ResizeHandle 
              position="sw" 
              onMouseDown={(e) => handleResizeStart(e, element.id, 'sw')}
            />
            <ResizeHandle 
              position="se" 
              onMouseDown={(e) => handleResizeStart(e, element.id, 'se')}
            />
            <RotateHandle onMouseDown={(e) => handleRotateStart(e, element.id)} />
          </>
        )}
      </CanvasElement>
    );
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolSection>
          <ToolButton 
            active={activeTool === 'select'} 
            onClick={() => setActiveTool('select')}
          >
            <MousePointer size={16} />
            Selecionar
          </ToolButton>
          
          <ToolButton 
            active={activeTool === 'text'} 
            onClick={() => setActiveTool('text')}
          >
            <Type size={16} />
            Texto
          </ToolButton>
          
          <ToolButton 
            active={activeTool === 'rectangle'} 
            onClick={() => setActiveTool('rectangle')}
          >
            <Square size={16} />
            Retângulo
          </ToolButton>
          
          <ToolButton 
            active={activeTool === 'circle'} 
            onClick={() => setActiveTool('circle')}
          >
            <Circle size={16} />
            Círculo
          </ToolButton>
        </ToolSection>

        <ToolSection>
          <ToolButton onClick={() => setLayersVisible(!layersVisible)}>
            <Layers size={16} />
            Camadas
          </ToolButton>
        </ToolSection>
        
        {activeTool === 'text' && (
          <ToolSection>
            <StyledInput
              placeholder="Digite o texto..."
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              style={{ width: '150px' }}
            />
            <input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              style={{ width: '40px', height: '32px' }}
            />
          </ToolSection>
        )}
        
        {(activeTool === 'rectangle' || activeTool === 'circle') && (
          <ToolSection>
            <input
              type="color"
              value={shapeColor}
              onChange={(e) => setShapeColor(e.target.value)}
              style={{ width: '40px', height: '32px' }}
            />
          </ToolSection>
        )}
      </Toolbar>
      
      <MainArea>
        <CanvasArea layersVisible={layersVisible}>
          <AnimatedBackground>
            <Squares 
              direction="diagonal"
              speed={0.3}
              squareSize={30}
              borderColor="rgba(255, 255, 255, 0.1)" 
              hoverFillColor="rgba(255, 255, 255, 0.05)"
            />
          </AnimatedBackground>
          
          <Canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {sortedElements.map(renderElement)}
          </Canvas>
        </CanvasArea>
        
        <LayersToggle 
          visible={layersVisible}
          onClick={() => setLayersVisible(!layersVisible)}
        >
          {layersVisible ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </LayersToggle>
        
        <LayersPanel visible={layersVisible}>
          <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#1f2937' }}>
            <Layers size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Camadas ({elements.length})
          </div>
          {sortedElements.reverse().map((element) => (
            <LayerItem
              key={element.id}
              selected={selectedElement === element.id}
              onClick={() => setSelectedElement(element.id)}
            >
              <span>
                {element.type === 'image' && <ImageIcon size={14} />}
                {element.type === 'text' && <Type size={14} />}
                {element.type === 'shape' && <Square size={14} />}
                <span style={{ marginLeft: '0.5rem' }}>
                  {element.type} #{element.id.slice(-4)}
                </span>
              </span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button onClick={(e) => { e.stopPropagation(); moveElementUp(element.id); }}>
                  <ArrowUp size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); moveElementDown(element.id); }}>
                  <ArrowDown size={12} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); deleteElement(element.id); }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </LayerItem>
          ))}
        </LayersPanel>
      </MainArea>
      
      {selectedElementData && (
        <PropertyPanel>
          <div style={{ color: '#1f2937', fontWeight: 600 }}>
            Elemento selecionado: {selectedElementData.type}
          </div>
          
          {selectedElementData.type === 'text' && (
            <>
              <StyledInput
                placeholder="Texto"
                value={selectedElementData.content || ''}
                onChange={(e) => updateSelectedElement({ content: e.target.value })}
                style={{ width: '150px' }}
              />
              
              <input
                type="number"
                value={selectedElementData.fontSize || 16}
                onChange={(e) => updateSelectedElement({ fontSize: parseInt(e.target.value) })}
                style={{ width: '60px', color: '#1f2937', background: 'white', border: '1px solid #d1d5db', borderRadius: '4px', padding: '0.5rem' }}
                min="8"
                max="72"
              />
              
              <StyledSelect>
                <Select 
                  value={selectedElementData.fontFamily || 'Arial'} 
                  onValueChange={(value) => updateSelectedElement({ fontFamily: value })}
                >
                  <SelectTrigger className="select-trigger" style={{ width: '120px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Arial">Arial</SelectItem>
                    <SelectItem value="Helvetica">Helvetica</SelectItem>
                    <SelectItem value="Times New Roman">Times</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                  </SelectContent>
                </Select>
              </StyledSelect>
              
              <input
                type="color"
                value={selectedElementData.color || '#000000'}
                onChange={(e) => updateSelectedElement({ color: e.target.value })}
                style={{ width: '40px', height: '32px' }}
              />
            </>
          )}
          
          {selectedElementData.type === 'shape' && (
            <input
              type="color"
              value={selectedElementData.backgroundColor || '#3B82F6'}
              onChange={(e) => updateSelectedElement({ backgroundColor: e.target.value })}
              style={{ width: '40px', height: '32px' }}
            />
          )}
          
          <div style={{ display: 'flex', gap: '0.5rem', color: '#1f2937', fontSize: '0.875rem' }}>
            <span>Rotação: {Math.round(selectedElementData.rotation)}°</span>
            <span>Z-Index: {selectedElementData.zIndex}</span>
            <span>Tamanho: {Math.round(selectedElementData.width)}x{Math.round(selectedElementData.height)}</span>
          </div>
        </PropertyPanel>
      )}
    </EditorContainer>
  );
};

export default AdvancedCanvasEditor;
